import { Controller } from "@hotwired/stimulus"

// The set-aside sheet (goal_contributions/new): Flexible and one budget side by
// side, each with its own amount. A row goes anywhere from 0 to what its
// source has free (data-max), and the two together never pass what the goal
// still needs (goalRoom). The server holds the same caps on save.
//
// Each row is typed or stepped, or all cleared to 0 at once; the button and the line under it always say
// the total and where it comes from, so nothing is set aside from a source the
// user did not see named.
export default class extends Controller {
  static targets = ["input", "field", "minus", "plus", "submit", "note", "fill", "clear"]

  static values = {
    locale: String,
    goalRoom: Number,
    owed: Number,
    pace: Number,
    inMonth: Number,
    symbol: String,
    symbolFirst: Boolean,
    i18n: Object
  }

  connect() {
    this.amounts = this.inputTargets.map((input) => this.#digits(input.value))
    this.render()
  }

  type(event) {
    const row = this.inputTargets.indexOf(event.target)
    this.amounts[row] = this.#capped(row, this.#digits(event.target.value))
    this.render()
  }

  step(event) {
    const row = parseInt(event.currentTarget.dataset.row, 10)
    const delta = parseInt(event.currentTarget.dataset.step, 10) || 0
    this.amounts[row] = this.#capped(row, Math.max(this.amounts[row] + delta, 0))
    this.render()
  }

  // Clear: every row back to 0.
  clear() {
    this.amounts = this.amounts.map(() => 0)
    this.render()
  }

  render() {
    this.inputTargets.forEach((input, row) => {
      const amount = this.amounts[row]
      if (document.activeElement !== input || this.#digits(input.value) !== amount) {
        input.value = String(amount)
      }
      this.fieldTargets[row].value = amount
      this.minusTargets[row].disabled = amount <= 0
      this.plusTargets[row].disabled = amount >= this.#rowMax(row)
    })

    const total = this.#total()
    const t = this.i18nValue
    // No submit target when access has lapsed: the access-gate button stands in.
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = total <= 0
      this.submitTarget.value = total > 0 ? t.submit.replace("%{amount}", this.#money(total)) : t.submit_empty
    }
    if (this.hasClearTarget) this.clearTarget.disabled = total <= 0

    const parts = this.inputTargets
      .map((input, row) => this.amounts[row] > 0 && t.part
        .replace("%{amount}", this.#money(this.amounts[row]))
        .replace("%{source}", input.dataset.sourceName))
      .filter(Boolean)
    let note = parts.length ? t.will_save.replace("%{parts}", parts.join(" + ")) : ""
    if (parts.length && total > this.owedValue) note += ` ${t.ahead}`
    this.noteTarget.textContent = note

    // This month toward the pace: what is already in, plus what is about to go.
    if (this.hasFillTarget && this.paceValue > 0) {
      const pct = Math.min(((this.inMonthValue + total) / this.paceValue) * 100, 100)
      this.fillTarget.style.width = `${Math.max(pct, 2)}%`
    }
  }

  // ── Internals ──

  #rowMax(row) {
    const own = parseInt(this.inputTargets[row].dataset.max, 10) || 0
    const others = this.#total() - this.amounts[row]
    return Math.max(Math.min(own, this.goalRoomValue - others), 0)
  }

  #capped(row, value) {
    return Math.min(value, this.#rowMax(row))
  }

  #total() {
    return this.amounts.reduce((sum, amount) => sum + amount, 0)
  }

  // Whole units only: Arabic-Indic digits become ASCII, and anything after a
  // decimal separator is dropped (so "12.50" is 12, not 1250).
  #digits(value) {
    const ascii = String(value)
      .replace(/[٠-٩]/g, (d) => d.charCodeAt(0) - 0x0660)
      .replace(/[۰-۹]/g, (d) => d.charCodeAt(0) - 0x06F0)
    const digits = ascii.split(/[.,٫]/)[0].replace(/\D/g, "").slice(0, 9)
    return digits ? parseInt(digits, 10) : 0
  }

  #money(amount) {
    const number = amount.toLocaleString(this.localeValue)
    return this.symbolFirstValue ? `${this.symbolValue}${number}` : `${number}${this.symbolValue}`
  }
}
